import EntityService from "@/services/entityService";

const services = [
  new EntityService("StockBalance"),
  new EntityService("stockbalance")
];
const fisDocRecService = new EntityService("FisDocRec");
const fisDocRecItemService = new EntityService("FisDocRecItem");
const ordemDeServicoItensService = new EntityService("OrdemDeServicoItens");

const toNumber = (valor) => {
  if (valor === null || valor === undefined || valor === "") return 0;
  if (typeof valor === "number") {
    return Number.isFinite(valor) ? valor : 0;
  }

  const textoOriginal = String(valor).trim();
  if (!textoOriginal) return 0;

  const textoLimpo = textoOriginal.replace(/[^0-9,.-]/g, "");
  const ultimaVirgula = textoLimpo.lastIndexOf(",");
  const ultimoPonto = textoLimpo.lastIndexOf(".");

  let textoNumero = textoLimpo;

  if (ultimaVirgula > -1 && ultimoPonto > -1) {
    if (ultimoPonto > ultimaVirgula) {
      textoNumero = textoLimpo.replace(/,/g, "");
    } else {
      textoNumero = textoLimpo.replace(/\./g, "").replace(/,/g, ".");
    }
  } else if (ultimaVirgula > -1) {
    textoNumero = textoLimpo.replace(/\./g, "").replace(/,/g, ".");
  } else {
    const partesPonto = textoLimpo.split(".");
    if (partesPonto.length > 2) {
      const ultimaParte = partesPonto.pop();
      textoNumero = `${partesPonto.join("")}.${ultimaParte}`;
    }
  }

  const numero = Number(textoNumero);
  return Number.isFinite(numero) ? numero : 0;
};

const toText = (valor, fallback = "-") => {
  if (valor === null || valor === undefined) return fallback;
  const texto = String(valor).trim();
  return texto || fallback;
};

const obterCampoTexto = (origem, campos = []) => {
  if (!origem || typeof origem !== "object") return "";

  for (const campo of campos) {
    const valor = origem?.[campo];
    if (valor !== null && valor !== undefined && String(valor).trim() !== "") {
      return String(valor).trim();
    }
  }

  return "";
};

const extrairCodigoErp = (linha = {}) => {
  const itemObj = Array.isArray(linha?.item) ? linha.item[0] : linha?.item;
  const codigoRaiz = obterCampoTexto(linha, ["itemCodigo"]);
  if (codigoRaiz) return codigoRaiz;

  const codigoItem = obterCampoTexto(itemObj, ["itemCodigo"]);
  return codigoItem || "-";
};

const toDate = (valor) => {
  if (!valor) return null;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
};

const normalizarStatus = (valor) => {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

const statusRecebimentoValido = (status) => {
  const normalizado = normalizarStatus(status);
  return normalizado === "recebida" || normalizado === "recebida parcialmente";
};

const normalizarCodigo = (valor) => String(valor || "").trim().toLowerCase();
const normalizarDescricao = (valor) => String(valor || "").trim().toLowerCase();

const extrairCodigoItemGenerico = (origem = {}) => {
  const itemObj = Array.isArray(origem?.item) ? origem.item[0] : origem?.item;

  return (
    obterCampoTexto(origem, ["itemCodigo", "codigoItem", "codigo", "itemCode"]) ||
    obterCampoTexto(itemObj, ["itemCodigo", "codigoItem", "codigo", "itemCode"])
  );
};

const extrairDescricaoItemGenerica = (origem = {}) => {
  const itemObj = Array.isArray(origem?.item) ? origem.item[0] : origem?.item;

  return (
    obterCampoTexto(origem, ["descriptionItem", "descricao", "descricaoItem", "itemDescricao", "itemName"]) ||
    obterCampoTexto(itemObj, ["descricao", "description", "itemDescricao", "itemName"])
  );
};

const extrairListaComFallback = async (service, limites = [5000, 10000, 20000]) => {
  for (const limit of limites) {
    try {
      const response = await service.getAll(1, limit, "", "", null, null, null, null);
      const lista = extrairLista(response);
      if (Array.isArray(lista)) return lista;
    } catch (error) {
      // tenta próximo limite
    }
  }

  return [];
};

const criarMapasDeMovimento = (listaBase = [], listaOsItens = [], listaFisDocRec = [], listaFisDocRecItem = []) => {
  const descricaoParaCodigo = new Map();

  for (const linhaBase of listaBase) {
    const codigo = normalizarCodigo(extrairCodigoErp(linhaBase));
    const descricao = normalizarDescricao(
      linhaBase.descricao || linhaBase.description || linhaBase.itemDescricao || linhaBase.itemName
    );

    if (codigo && descricao && !descricaoParaCodigo.has(descricao)) {
      descricaoParaCodigo.set(descricao, codigo);
    }
  }

  const seqParaCodigo = new Map();
  const saidaPorCodigo = new Map();

  for (const osItem of Array.isArray(listaOsItens) ? listaOsItens : []) {
    const codigoExtraido = extrairCodigoItemGenerico(osItem);
    const descricaoExtraida = extrairDescricaoItemGenerica(osItem);

    const codigo = normalizarCodigo(codigoExtraido) || descricaoParaCodigo.get(normalizarDescricao(descricaoExtraida)) || "";

    const sequencia = String(osItem?.sequencia ?? "").trim();
    if (sequencia && codigo && !seqParaCodigo.has(sequencia)) {
      seqParaCodigo.set(sequencia, codigo);
    }

    if (codigo) {
      const quantidadeEntregue = toNumber(osItem?.quantidadeEntregue || 0);
      if (quantidadeEntregue) {
        saidaPorCodigo.set(codigo, (saidaPorCodigo.get(codigo) || 0) + quantidadeEntregue);
      }
    }
  }

  const idsFisDocRecItemValidos = new Set();
  for (const nota of Array.isArray(listaFisDocRec) ? listaFisDocRec : []) {
    if (!statusRecebimentoValido(nota?.status)) continue;
    const ids = nota?.fisDocRecItemS;
    if (!Array.isArray(ids)) continue;
    for (const id of ids) {
      const rawId = typeof id === "object" ? id?._id || id?.id : id;
      if (rawId) idsFisDocRecItemValidos.add(String(rawId));
    }
  }

  const entradaPorCodigo = new Map();
  for (const item of Array.isArray(listaFisDocRecItem) ? listaFisDocRecItem : []) {
    const itemId = String(item?._id || item?.id || "");
    if (!itemId || !idsFisDocRecItemValidos.has(itemId)) continue;

    const codigoDireto = normalizarCodigo(extrairCodigoItemGenerico(item));
    const codigoPorSeq = seqParaCodigo.get(String(item?.seqItemPurchaseOrder ?? "").trim()) || "";
    const codigoPorDescricao = descricaoParaCodigo.get(normalizarDescricao(extrairDescricaoItemGenerica(item))) || "";
    const codigo = codigoDireto || codigoPorSeq || codigoPorDescricao;

    if (!codigo) continue;

    const quantidade = toNumber(item?.qty || item?.quantidade || item?.qtyReceived || 0);
    if (!quantidade) continue;

    entradaPorCodigo.set(codigo, (entradaPorCodigo.get(codigo) || 0) + quantidade);
  }

  return { entradaPorCodigo, saidaPorCodigo };
};

const normalizarLinha = (linha = {}) => {
  const qtdInicial = toNumber(linha.qtdInicial || linha.quantidadeInicial || linha.estoqueInicial || linha.quantidadeTotal || 0);
  const qtdEntrada = toNumber(linha.qtdEntrada || linha.entrada || linha.entradas || linha.quantidadeEntrada || 0);
  const qtdSaida = toNumber(linha.qtdSaida || linha.saida || linha.saidas || linha.quantidadeSaida || 0);
  const qtdFinal = toNumber(linha.qtdFinal || linha.estoqueFinal || linha.saldo || linha.quantidadeFinal || linha.quantidadeDisponivel || qtdInicial);

  const vlrUnitario = toNumber(linha.valorReal || linha.vlrUnitario || linha.valorUnitario || linha.custoMedio || linha.valorMedio || 0);
  const precoMedio = toNumber(linha.valorMedio || linha.precoMedio || linha.custoMedio || linha.vlrMedio || 0);
  const vlrTotal = toNumber(qtdInicial * vlrUnitario);

  return {
    _id: linha._id || linha.id || null,
    data: linha.updateAt || linha.updatedAt || linha.lastUpdate || linha.dataUltimaAlteracao || linha.data || linha.date || linha.dataMovimento || linha.dataLancamento || linha.createAt || null,
    codigoItem: extrairCodigoErp(linha),
    descricao: toText(linha.descricao || linha.description || linha.itemDescricao || linha.itemName),
    qtdInicial,
    qtdEntrada,
    qtdSaida,
    qtdFinal,
    vlrUnitario,
    precoMedio,
    vlrTotal,
    ncm: toText(linha.ncm || linha.NCM)
  };
};

const extrairLista = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const listarPorEntidade = async () => {
  for (const service of services) {
    try {
      const response = await service.getAll(1, 5000, "", "", null, null, null, null);
      const lista = extrairLista(response);
      if (lista.length) return lista;
    } catch (error) {
      // tenta próximo schema
    }
  }

  return [];
};

export default {
  async listarRelatorioEstoque(filtros = {}) {
    try {
      const [lista, listaOsItens, listaFisDocRec, listaFisDocRecItem] = await Promise.all([
        listarPorEntidade(),
        extrairListaComFallback(ordemDeServicoItensService),
        extrairListaComFallback(fisDocRecService),
        extrairListaComFallback(fisDocRecItemService)
      ]);

      const { entradaPorCodigo, saidaPorCodigo } = criarMapasDeMovimento(
        lista,
        listaOsItens,
        listaFisDocRec,
        listaFisDocRecItem
      );

      const inicio = filtros.dataInicio ? toDate(filtros.dataInicio) : null;
      const fim = filtros.dataFim ? toDate(filtros.dataFim) : null;
      if (fim) fim.setHours(23, 59, 59, 999);

      const codigoFiltro = String(filtros.codigoItem || "").trim().toLowerCase();
      const descricaoFiltro = String(filtros.descricaoItem || "").trim().toLowerCase();

      return (Array.isArray(lista) ? lista : [])
        .map((linhaBase) => {
          const itemNormalizado = normalizarLinha(linhaBase);
          const codigo = normalizarCodigo(itemNormalizado.codigoItem);
          const qtdEntrada = entradaPorCodigo.get(codigo) || 0;
          const qtdSaida = saidaPorCodigo.get(codigo) || 0;

          return {
            ...itemNormalizado,
            qtdEntrada,
            qtdSaida,
            qtdFinal: toNumber(itemNormalizado.qtdInicial) - qtdSaida
          };
        })
        .filter((item) => {
          const dataItem = toDate(item.data);
          const dataOk = (!inicio || (dataItem && dataItem >= inicio)) && (!fim || (dataItem && dataItem <= fim));
          const codigoOk = !codigoFiltro || String(item.codigoItem || "").toLowerCase().includes(codigoFiltro);
          const descricaoOk = !descricaoFiltro || String(item.descricao || "").toLowerCase().includes(descricaoFiltro);
          return dataOk && codigoOk && descricaoOk;
        });
    } catch (erro) {
      console.error("Erro ao listar relatório de estoque:", erro);
      return [];
    }
  }
};
